import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralRoadmap = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [roadmap, setRoadmap] = useState([]);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const initialForm = {
    quarter: "",
    goal: "",
    owner: "",
    status: "Planned",
  };

  const [formData, setFormData] = useState(initialForm);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // -------------------------
  // Sample Data
  // -------------------------

  const sampleRoadmap = [
    {
      id: 1,
      quarter: "Q1 2026",
      goal: "Launch Mobile App",
      owner: "Product Team",
      status: "Completed",
    },
    {
      id: 2,
      quarter: "Q2 2026",
      goal: "AI Analytics Dashboard",
      owner: "AI Team",
      status: "Active",
    },
    {
      id: 3,
      quarter: "Q3 2026",
      goal: "International Expansion",
      owner: "Strategy Team",
      status: "Planned",
    },
  ];

  const loadSample = () => {
    setRoadmap(sampleRoadmap);
  };

  // -------------------------
  // CRUD
  // -------------------------

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);

    setFormData({
      quarter: item.quarter,
      goal: item.goal,
      owner: item.owner,
      status: item.status,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete roadmap item?")) return;

    setRoadmap((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newItem = {
      id: editingItem?.id || Date.now(),
      quarter: formData.quarter,
      goal: formData.goal,
      owner: formData.owner,
      status: formData.status,
    };

    if (editingItem) {
      setRoadmap((prev) =>
        prev.map((r) => (r.id === editingItem.id ? newItem : r)),
      );
    } else {
      setRoadmap((prev) => [...prev, newItem]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // -------------------------
  // Search
  // -------------------------

  const filtered = roadmap.filter(
    (r) =>
      r.goal.toLowerCase().includes(search.toLowerCase()) ||
      r.quarter.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  const statusColor = {
    Planned: "bg-indigo-600",
    Active: "bg-emerald-600",
    Completed: "bg-gray-600",
    Delayed: "bg-red-600",
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">
      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            🗺 SprintCentral Roadmap
          </div>
          <div className="text-xs text-gray-400">
            Strategic Product Planning
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>

              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>

    <div className="mt-2 mb-2 min-h-[70vh]">
      {/* TOOLBAR */}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">
            <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-56 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
            >
            <FontAwesomeIcon icon={faPlus} /> Sample Roadmap
            </button>

            <button
            onClick={handleAddNew}
            className="text-xs lg:text-sm w-56 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
            >
            <FontAwesomeIcon icon={faPlus} /> New Roadmap Item
            </button>

            <div className="relative w-full">
            <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
                type="text"
                placeholder="Search roadmap..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 w-full bg-gray-800 border border-gray-700 rounded-lg"
            />
            </div>
        </div>

        {/* MODAL */}

        {showModal && (
            <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg text-cyan-200 font-semibold">
                {editingItem ? "Edit Roadmap Item" : "New Roadmap Item"}
                </h2>

                <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
                >
                <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
                <input
                type="text"
                placeholder="Quarter (Example: Q1 2026)"
                value={formData.quarter}
                required
                onChange={(e) =>
                    setFormData({ ...formData, quarter: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
                />

                <textarea
                placeholder="Strategic Goal"
                value={formData.goal}
                style={{ height: "100px" }}
                onChange={(e) =>
                    setFormData({ ...formData, goal: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
                />

                <input
                type="text"
                placeholder="Owner Team"
                value={formData.owner}
                onChange={(e) =>
                    setFormData({ ...formData, owner: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
                />

                <select
                value={formData.status}
                onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                }
                className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2"
                >
                <option value="Planned">Planned</option>
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Delayed">Delayed</option>
                </select>

                <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
                >
                {editingItem ? "Update" : "Create"}
                </button>
            </form>
            </div>
        )}

        {/* GRID */}

        <div className="mt-6 grid gap-4 grid-cols-1 lg:grid-cols-4">
            {paginated.map((item) => (
            <div
                key={item.id}
                className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >
                <div className="flex justify-between items-start">
                <h3 className="font-semibold text-white">{item.quarter}</h3>

                <span
                    className={`px-2 py-1 rounded text-xs ${statusColor[item.status]}`}
                >
                    {item.status}
                </span>
                </div>

                <p className="text-sm text-gray-300 mt-2">{item.goal}</p>

                <div className="text-xs text-gray-400 mt-3">
                Owner: {item.owner || "-"}
                </div>

                <div className="mt-4 flex gap-2">
                <button
                    onClick={() => handleEdit(item)}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
                </div>
            </div>
            ))}
        </div>

        {/* PAGINATION */}

        {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-10">
            <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
                <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span className="text-sm">
                Page {currentPage} of {totalPages}
            </span>

            <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
                <FontAwesomeIcon icon={faChevronRight} />
            </button>
            </div>
        )}
    </div>

      {/* FOOTER */}

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-10">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralRoadmap;
